<template>
  <config-base :config-plan-tile-status="configPlanTileStatus" class="video-management-config" @view-details="showPlanDetailDialog">
    <div slot="config">
      <config-cycle
        v-model="billingCycle"
        :show-cycle-alert="!basePlanInSelected"
        :showMonthlyPriceFromCycle="true"
        :billing-cycle-list="billingCycleList"
        :unit="unit"
        @switchCycleAction="updateBillingCycle"
      />
      <div class="video-management-config__excess-notice">
        {{ $t('billing.video_management.excess_charge_notice', {
              month_price: excessChargeInfo.month_price,
              annual_price: excessChargeInfo.annual_price,
              viewers: excessChargeInfo.include_unit,
              each_price: $money(excessChargeInfo.overage_amount),
              base_plan_name: excessChargeInfo.base_plan_name,
          })
        }}
      </div>
      <div class="video-management-config__need-more">
        {{ $t('billing.video_management.need_more') }}
        <zm-button
          href="https://www.zoom.com/en/contact/contact-sales-c"
          type="link"
          target="_blank"
        >
          {{ $t('billing.common.contact_sales') }}
        </zm-button>
      </div>
      <preview-payment class="gap24" />
    </div>
    <template #footer v-if="isShowRemoveBtn">
      <zm-button
        data-testid="checkout-button-93"
        type="link"
        ghost
        @click="removePlanByKey"
        :aria-label="`remove ${title} plan`"
      >
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script>
import { get, chain, includes, pick } from 'lodash';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import ConfigBase from '@/components/config-base.vue';
import ConfigCycle from '@/components/service/config-billing-cycle/index.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import { plansof } from '@/utils/chain';
import { translateToEn } from '@/plugins/i18n';
import { getPlanTotalLicense } from '@/common/service/validate';
import plansMixin from '../mx/plan';
import addonMixin from '../mx/addon';
import planDetailsDialogMixin from './components/plan-details/plan-details-dialog';
import commonMixin from './mixin';
import { flowTypes } from '@/common/service/page-config';
import { planUnitMap } from '@/common/product-unit';
import { dependBasePlanTypeList } from "./utils"
import autofill from "@/plans/mx/autofill";
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import baseplanFreeToPaidReplenishMixin from "@/components/capacity/mx/freetrial-baseplan-replenish";

const ConfigBaseComp = {
  name: 'VideoManagementBase',
  extends: ConfigBase,
  mixins: [commonMixin],
  key: planTypes[planTypes.VIDEO_MANAGEMENT],
  data() {
    return {
      showLearnMore: false,
      showPlanDetails: true,
      unit: planUnitMap.license,
      trackingPlanName: 'zoom video management',
    };
  },
};

export default {
  name: 'VideoManagementConfig',
  mixins: [commonMixin, plansMixin, addonMixin, planDetailsDialogMixin, autofill, baseplanFreeToPaidReplenishMixin],
  components: { ConfigBase: ConfigBaseComp, ConfigCycle, PreviewPayment },
  data() {
    return {
      billingCycle: '',
      licenseNumber: 1,
      unit: planUnitMap.license,
      BILLING_CYCLE
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'getCurrentBasePlanInfo',
      'getUserCurrentSubscriptions',
      'basicConfig',
      'currentEditPlan',
      'getSubscriptions',
    ]),

    excessChargeInfo() {
      const billingCycleAmountMap = this.planList.reduce((prev,cur) => {
        prev[cur.billing_cycle] = cur.amount;
        return prev;
      }, {});
      const annualAmountMap = this.$priceByMonth(billingCycleAmountMap[BILLING_CYCLE.ANNUAL], BILLING_CYCLE.ANNUAL, { forceDiff: true });
      return {
        ...billingCycleAmountMap,
        month_price: this.$money(billingCycleAmountMap[BILLING_CYCLE.MONTHLY], { forceDiff: true }),
        annual_price: `${annualAmountMap.currency.symbol}${annualAmountMap.integer}`,
        include_unit: this.selectedPlan.include_unit,
        overage_amount: this.selectedPlan.overage_amount,
        base_plan_name: this.$t(this.currentBasePlan.planNameKey),
      }
    },

    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },

    currentPlanBasicInfo() {
      return pick(this.planList[0], ['min', 'max', 'unit', 'category']);
    },

    currentBasePlan() {
      return get(this.getCurrentBasePlanInfo, ['plans', 0], {});
    },

    getCurrentBasePlanInfoList() {
      let plans = this.getSelectedPlans ?? [];
      if (![flowTypes.buyNow, flowTypes.basic2pro].includes(window.$context.flow)) {
        plans = this.getUserCurrentSubscriptions.filter((item) => !item.isFreeTrial) ?? [];
      }

      const billingCycleDependBasePlanTypeList = this.selectedPlan?.billingCycleDependBasePlanTypeList ?? [];
      const basePlans = plans.filter((item) => billingCycleDependBasePlanTypeList.includes(item.type));
      return basePlans;
    },

    basePlanTotalLicenses() {
      const plans = this.getCurrentBasePlanInfoList;
      return getPlanTotalLicense(plans);
    },

    /**
     * implementing within plansMixin
     * disable billing cycle
     */
    currentPlanCycleDepFirstPlan() {
      const dependCyclePlanList = dependBasePlanTypeList;
      return chain(this.getUserCurrentSubscriptions)
        .filter((p) => includes(dependCyclePlanList, p.type))
        .orderBy((p) => p.cycleValue, 'desc')
        .get(0)
        .value();
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicense() {
      return (billingCycle) => {
        return (
          this.isEditFlow &&
          this.basicConfig?.isBulkLicenseAccount &&
          this.currentEditPlan?.billing_cycle !== billingCycle
        );
      };
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicenseTips() {
      return this.$t('billing.error.buld_acount_license');
    },

    /**
     * Is current base plan in selected plans
     */
    basePlanInSelected() {
      return this.getSelectedPlans.some((plan) => plan.type === this.currentBasePlan.type);
    },

    planTrackingName() {
      return translateToEn(this.selectedPlan?.planNameKey);
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),

    /**
     * update billing cycle
     * @param {object} oldPlan
     * @param {object} newPlan
     */
    async updateBillingCycle(oldPlan, newPlan) {
      this.$tracking.buyInteraction('subscription cycle', `${this.planTrackingName} - ${newPlan.billing_cycle}`);

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        { ...newPlan, cartInfo: { selected_number: this.licenseNumber } },
      ]);
    },
  },

  created() {
    if (this.selectedPlan?.billing_cycle) {
      this.billingCycle = this.selectedPlan.billing_cycle;
      this.licenseNumber = 1;

      // refresh page
      this.$nextTick(() => {
        this.mx_validDownSellLicense(this.licenseNumber);
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
@import '@/styles/zoom-card.scss';

$sub-font-color: #666487;
$main-font-color: #00031f;

.main-color {
  color: $theme-color;
}

.gap8 {
  margin-top: 8px;
}
.gap16 {
  margin-top: 16px;
}
.gap24 {
  margin-top: 24px;
}

.video-management-config {
  /deep/ .opc-zm-base-card {
    &__name {
      text-transform: none;
    }
    &__main--desc__content {
      font-size: 16px;
      color: $sub-font-color;
    }
  }

  &__item-label {
    font-size: 18px;
    font-weight: 700;
    color: $main-font-color;
    line-height: 27px;
  }

  &__info-icon {
    padding-left: 4px;
    font-size: 16px;
    line-height: 27px;
  }

  &__tips-content {
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: #747487;
  }

  &__licenses-info {
    color: $sub-font-color;
    font-size: 14px;
    line-height: 16.8px;
  }

  &__license-alert {
    padding: 16px;
    color: $sub-font-color;
    font-size: 14px;
    line-height: 21px;
    a {
      font-weight: 450;
    }
  }

  &__excess-notice, &__need-more {
    margin-top: 24px;
    color: #666487;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
  }
}
</style>
